// Live-polling banner for an in-flight async document generation. Mounted by
// ContractEditor whenever the contract's generationRequestedAt is ahead of
// documentGeneratedAt/generationError (see ContractEditor's generationInFlight
// derivation). Polls GET /contracts/{id}/generation-status via
// useGenerationStatus and renders one of three states: pending ("Generating
// document…" with a subtle progress affordance), completed (a "Download
// document" button that opens the presigned URL from a real click — a user
// gesture, so no popup blocking), or failed (mirrors ContractEditor's legacy
// failed-banner styling with the real error line). The completed state also
// renders an amber warnings inset when the run produced fail-open warnings
// (e.g. a partial multi-select drop); the Download button stays available
// either way. On the transition to completed it invalidates the contract
// detail query exactly once, via a
// useEffect with a previous-status guard, so the page's status flip and new
// document version row appear without a manual refresh.
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useGenerationStatus, contractKeys } from '../../hooks/useContracts';
import type { GenerationResult } from '../../types';
import Alert from './Alert';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';

interface GenerationStatusBannerProps {
  contractId: string;
  inFlight: boolean;
}

export default function GenerationStatusBanner({
  contractId,
  inFlight,
}: GenerationStatusBannerProps) {
  const queryClient = useQueryClient();
  const { data } = useGenerationStatus(contractId, inFlight);
  const status = data?.status;
  const prevStatusRef = useRef<GenerationResult['status'] | undefined>(status);

  useEffect(() => {
    if (prevStatusRef.current !== 'completed' && status === 'completed') {
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(contractId),
      });
    }
    prevStatusRef.current = status;
  }, [status, contractId, queryClient]);

  if (status === 'completed') {
    const warnings = data?.warnings;
    return (
      <Alert variant="success">
        <p className="font-medium">Document generated</p>
        <p className="mt-1">Your document is ready to download.</p>
        {warnings && warnings.length > 0 && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
            <p className="font-medium">
              {`Generated with ${warnings.length} warning${
                warnings.length === 1 ? '' : 's'
              }`}
            </p>
            <ul className="mt-1 space-y-1">
              {warnings.map((warning, index) => (
                <li key={index} className="font-mono text-body-sm">
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          icon={<Download className="h-4 w-4" />}
          className="mt-2"
          onClick={() => {
            if (data?.documentUrl) {
              window.open(data.documentUrl, '_blank');
            }
          }}
        >
          Download document
        </Button>
      </Alert>
    );
  }

  if (status === 'failed') {
    return (
      <Alert variant="warning">
        <p className="font-medium">Document generation failed</p>
        <p className="mt-1">
          The document could not be generated automatically.
        </p>
        {data?.error && (
          <p className="mt-1 font-mono text-body-sm">{data.error}</p>
        )}
      </Alert>
    );
  }

  // Pending, or still fetching the first status while a generation is in
  // flight — same subtle "in progress" affordance either way.
  return (
    <Alert variant="info">
      <div className="flex items-center gap-3">
        <LoadingSpinner size="sm" />
        <span className="font-medium">Generating document…</span>
      </div>
    </Alert>
  );
}
